import { ConfigurationSettings } from "models/configuration-settings";
import { clients } from "lib/https";
import { v4 as uuidv4 } from 'uuid';
import {
    PendingQuantityChange,
    SubscriptionDetails,
    SubscriptionMeterTerms,
    SubscriptionQuantity,
} from "state/store/subscription/subscriptionStore";
import { ApiPlan } from "../components/subscription-plan";

export type CurrentSubscriptionMeta = {
    correlationId: string;
    timestampUtc: string;
    replayed: boolean;
};

export type CurrentSubscriptionResponse = {
    data: SubscriptionDetails | null;
    error: string | null;
    meta: CurrentSubscriptionMeta;
};


export const contactSales = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.contactSales}`;

    const payload = {
        ...data,
        ProjectKey: config.blocksKey,
    };

    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const createPayment = async (
    data: any,
    config: ConfigurationSettings
) => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.paymentCreate}`;

    const response = clients.post(url, config.blocksKey, JSON.stringify(data), { 'idempotency-key': uuidv4() });

    return response;
};

export const makeSubscription = async (
    data: any,
    config: ConfigurationSettings
) => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.subscriptions}`;

    const response = clients.post(url, config.blocksKey, JSON.stringify(data));

    return response;
};

export const getSubscriptionPlans = async (
    organizationId: string,
    config: ConfigurationSettings
) => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    const params = new URLSearchParams({ organizationId });
    const url = `${baseUrl}${config.paths.businessService.subscriptionPlans}?${params.toString()}`;

    return clients.get<{ data: ApiPlan[] }>(url, config.blocksKey);
};

export const cancelSubscription = async (
    subscriptionId: string,
    config: ConfigurationSettings
) => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.subscriptions}/${subscriptionId}?immediately=true`;

    return clients.delete(url, config.blocksKey);
};

export type ChangeSubscriptionPlanRequest = {
    subscriptionId: string;
    planCode: string;
    priceId: string;
    quantities?: { itemKey: string; quantity: number }[];
    interval?: string;
};

export const changeSubscriptionPlan = async (
    data: ChangeSubscriptionPlanRequest,
    config: ConfigurationSettings
) => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.subscriptions}/${data.subscriptionId}/plan`;
    const body = {
        planCode: data.planCode,
        priceId: data.priceId,
        quantities: data.quantities ?? [],
    };

    return clients.put(url, config.blocksKey, JSON.stringify(body));
};

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
    value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : undefined;

const readString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined;

const readNumber = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const readBoolean = (value: unknown): boolean | undefined =>
    typeof value === 'boolean' ? value : undefined;

const readQuantities = (value: unknown): SubscriptionQuantity[] | undefined => {
    if (!Array.isArray(value)) return undefined;
    return value.flatMap((item) => {
        const record = asRecord(item);
        if (!record) return [];
        const itemKey = readString(record.itemKey);
        const quantity = readNumber(record.quantity);
        if (!itemKey || quantity === undefined) return [];
        return [{
            itemKey,
            unitLabel: readString(record.unitLabel) ?? itemKey,
            quantity,
        }];
    });
};

const readMeters = (value: unknown): SubscriptionMeterTerms[] | undefined => {
    if (!Array.isArray(value)) return undefined;
    return value.flatMap((item) => {
        const record = asRecord(item);
        const meterKey = readString(record?.meterKey);
        if (!record || !meterKey) return [];
        return [{
            meterKey,
            unitLabel: readString(record.unitLabel) ?? meterKey,
            quantityScale: readNumber(record.quantityScale) ?? 0,
            includedQuantity: readNumber(record.includedQuantity) ?? 0,
        }];
    });
};

const readPendingQuantityChange = (value: unknown): PendingQuantityChange | null => {
    if (value == null) return null;
    const record = asRecord(value);
    if (!record) return null;
    const quantities = readQuantities(record.quantities);
    const timing = readString(record.timing);
    return {
        timing: timing === 'Immediate' || timing === 'NextRenewal' ? timing : undefined,
        effectiveAtUtc: readString(record.effectiveAtUtc) ?? readString(record.effectiveAt) ?? null,
        quantities,
    };
};

const unwrapSubscriptionRecord = (res: unknown): Record<string, unknown> | undefined => {
    let current: unknown = res;
    for (let i = 0; i < 4; i++) {
        const record = asRecord(current);
        if (!record) return undefined;
        if (readString(record.subscriptionId) || readString(record.id)) {
            return record;
        }
        if ('data' in record) {
            const nested = record.data;
            current = Array.isArray(nested) ? nested[0] : nested;
            continue;
        }
        return record;
    }
    return asRecord(current);
};

export const mapCurrentSubscription = (res: unknown): SubscriptionDetails | null => {
    const subData = unwrapSubscriptionRecord(res);
    const subscriptionId = readString(subData?.subscriptionId) ?? readString(subData?.id);
    if (!subData || !subscriptionId) {
        return null;
    }

    const nestedPrice = asRecord(subData.price);
    const nestedPlan = asRecord(subData.plan);
    const currentPeriodEndUtc =
        readString(subData.currentPeriodEndUtc) ??
        readString(subData.currentPeriodEnd) ??
        readString(subData.periodEnd) ??
        readString(subData.validUntil) ??
        null;

    return {
        subscriptionId,
        status: readString(subData.status),
        planCode: readString(subData.planCode) ?? readString(nestedPlan?.code) ?? '',
        planName:
            readString(subData.planName) ??
            readString(subData.displayName) ??
            readString(nestedPlan?.displayName) ??
            readString(nestedPlan?.name) ??
            '',
        currencyCode: readString(subData.currencyCode),
        unitAmountMinor: readNumber(subData.unitAmountMinor),
        interval: readString(subData.interval) ?? readString(nestedPrice?.interval),
        intervalCount: readNumber(subData.intervalCount),
        displayPriceNote: readString(subData.displayPriceNote) ?? null,
        quantities: readQuantities(subData.quantities),
        currentPeriodStartUtc: readString(subData.currentPeriodStartUtc) ?? null,
        currentPeriodEndUtc,
        nextPaymentAtUtc: readString(subData.nextPaymentAtUtc) ?? null,
        trialEndsAtUtc: readString(subData.trialEndsAtUtc) ?? null,
        cancelAtPeriodEnd: Boolean(subData.cancelAtPeriodEnd ?? subData.cancelAt),
        canceledAtUtc: readString(subData.canceledAtUtc) ?? null,
        pendingQuantityChange: readPendingQuantityChange(subData.pendingQuantityChange),
        currentTier: subData.currentTier ?? null,
        recurringAmountMinor: readNumber(subData.recurringAmountMinor),
        taxAmountMinor: readNumber(subData.taxAmountMinor),
        netAmountMinor: readNumber(subData.netAmountMinor),
        taxRateBasisPoints: readNumber(subData.taxRateBasisPoints) ?? null,
        taxMode: readString(subData.taxMode) ?? null,
        automaticDiscountBasisPoints: readNumber(subData.automaticDiscountBasisPoints) ?? null,
        quantityDiscountCombination: readString(subData.quantityDiscountCombination) ?? null,
        grossAmountMinor: readNumber(subData.grossAmountMinor),
        builtInDiscountMinor: readNumber(subData.builtInDiscountMinor),
        promotionalDiscountMinor: readNumber(subData.promotionalDiscountMinor),
        discountedAmountMinor: readNumber(subData.discountedAmountMinor),
        billingAlignment: readString(subData.billingAlignment),
        initialChargeAmountMinor: readNumber(subData.initialChargeAmountMinor),
        initialChargeProrated: readBoolean(subData.initialChargeProrated),
        prorationDays: readNumber(subData.prorationDays) ?? null,
        prorationTotalDays: readNumber(subData.prorationTotalDays) ?? null,
        checkoutUrl: readString(subData.checkoutUrl) ?? null,
        meters: readMeters(subData.meters),
        version: readNumber(subData.version),
        planId: readString(subData.planId) ?? readString(nestedPlan?.planId),
        priceId: readString(subData.priceId) ?? readString(nestedPrice?.priceId),
        currentPeriodEnd: currentPeriodEndUtc,
    };
};

export const getCurrentSubscription = async (
    config: ConfigurationSettings
) => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.businessService.currentSubscription}`;

    return clients.get<CurrentSubscriptionResponse>(url, config.blocksKey);
};